'use client';

import { useState, useCallback } from 'react';
import CountryCard from './CountryCard';
import FilterSection from './FilterSection';
import ComparisonTool from './ComparisonTool';

interface FilterState {
  region: string;
  language: string;
  budget: string;
  searchQuery: string;
}

interface Country {
  id: string;
  name: string;
  flag: string;
  flagAlt: string;
  heroImage: string;
  heroImageAlt: string;
  tagline: string;
  highlights: string[];
  currency: string;
  currencySymbol: string;
  studentCount: number;
  topUniversities: number;
  region: string;
  language: string;
  avgTuition: number;
  avgLiving: number;
  visaProcessing: string;
  workRights: string;
}

const countries: Country[] = [
{
  id: 'dubai',
  name: 'Dubai',
  flag: "https://images.unsplash.com/photo-1676970133027-f763acd59749",
  flagAlt: 'UAE flag with red, green, white, and black horizontal stripes',
  heroImage: "/assets/images/Dubai_luxury-1775186828985.jpg",
  heroImageAlt: 'Dubai skyline with Burj Khalifa tower and modern skyscrapers at night with lights',
  tagline: 'Luxury Living & Global Business Hub',
  highlights: [
    'Tax-free income – no income tax in UAE',
    '70+ accredited institutions in Dubai',
    'Golden Visa for exceptional graduates',
    'Gateway connecting East and West (200+ nationalities)',
  ],
  currency: 'AED',
  currencySymbol: 'AED',
  studentCount: 4100,
  topUniversities: 25,
  region: 'middle-east',
  language: 'english',
  avgTuition: 20000,
  avgLiving: 16000,
  visaProcessing: '2-4 weeks',
  workRights: '20 hrs/week with NOC'
},
{
  id: 'germany',
  name: 'Germany',
  flag: "https://images.unsplash.com/photo-1676286155310-1f7f39a85ed9",
  flagAlt: 'German flag with black, red, and gold horizontal stripes',
  heroImage: "https://images.unsplash.com/photo-1675467389773-b5a7b53f7df7",
  heroImageAlt: 'Historic Brandenburg Gate in Berlin with blue sky and tourists walking in front',
  tagline: 'Engineering Excellence & Tuition-Free Education',
  highlights: [
    'Tuition-free at public universities (only ~€350 semester fee)',
    'TUM ranked #37 globally (QS 2025)',
    '18-month post-study job-search visa',
    'EU Blue Card – permanent residency in 21 months',
  ],
  currency: 'EUR',
  currencySymbol: '€',
  studentCount: 8500,
  topUniversities: 45,
  region: 'europe',
  language: 'german',
  avgTuition: 0,
  avgLiving: 10800,
  visaProcessing: '6-12 weeks',
  workRights: '120 full days/year'
},
{
  id: 'singapore',
  name: 'Singapore',
  flag: "https://images.unsplash.com/photo-1562300735-b1f7f50e774b",
  flagAlt: 'Singapore flag with red and white horizontal stripes and crescent moon with stars',
  heroImage: "https://images.unsplash.com/photo-1627196380765-54b657304550",
  heroImageAlt: 'Modern Singapore skyline with Marina Bay Sands hotel and Gardens by the Bay at sunset',
  tagline: 'Asia\'s Education Hub & Innovation Center',
  highlights: [
    'NUS #8 & NTU #15 globally (QS 2025)',
    'English-medium instruction at all universities',
    'MOE Tuition Grant – up to 90% subsidy',
    'Employment Pass for post-study work',
  ],
  currency: 'SGD',
  currencySymbol: 'S$',
  studentCount: 3200,
  topUniversities: 12,
  region: 'asia',
  language: 'english',
  avgTuition: 28000,
  avgLiving: 15000,
  visaProcessing: '2-4 weeks',
  workRights: '16 hrs/week during studies'
},
{
  id: 'france',
  name: 'France',
  flag: "https://images.unsplash.com/photo-1675855508131-798d42b6f1ee",
  flagAlt: 'French flag with blue, white, and red vertical stripes',
  heroImage: "https://images.unsplash.com/photo-1714301283268-c2aa3292b19f",
  heroImageAlt: 'Eiffel Tower in Paris with Seine River and historic buildings during golden hour',
  tagline: 'Art, Culture & World-Class Education',
  highlights: [
    'Public university tuition: just €170–€600/year',
    'Sorbonne #59 & École Polytechnique #61 globally (QS 2025)',
    '1,700+ English-taught programs available',
    'APS post-study visa: 2 years for Master\'s graduates',
  ],
  currency: 'EUR',
  currencySymbol: '€',
  studentCount: 6200,
  topUniversities: 38,
  region: 'europe',
  language: 'french',
  avgTuition: 3000,
  avgLiving: 12000,
  visaProcessing: '4-6 weeks',
  workRights: '964 hrs/year (~20 hrs/week)'
},
{
  id: 'russia',
  name: 'Russia',
  flag: "https://images.unsplash.com/photo-1596970425791-896111698d85",
  flagAlt: 'Russian flag with white, blue, and red horizontal stripes',
  heroImage: "https://images.unsplash.com/photo-1648854751027-04266eea061c",
  heroImageAlt: 'Saint Basil Cathedral in Moscow Red Square with colorful onion domes against blue sky',
  tagline: 'Affordable Quality Education & Rich History',
  highlights: [
    'Tuition: ₽150,000–₽450,000/year ($1,600–$5,000)',
    'MSU ranked #87 globally (QS 2025)',
    'Russian Government Scholarship: 15,000 seats/year',
    'WHO-recognized medical degrees',
  ],
  currency: 'RUB',
  currencySymbol: '₽',
  studentCount: 5800,
  topUniversities: 28,
  region: 'europe',
  language: 'russian',
  avgTuition: 4000,
  avgLiving: 6000,
  visaProcessing: '3-5 weeks',
  workRights: 'Limited during studies'
},
{
  id: 'china',
  name: 'China',
  flag: "https://img.rocket.new/generatedImages/rocket_gen_img_11385f7ca-1766765040023.png",
  flagAlt: 'Chinese flag with red background and yellow stars',
  heroImage: "https://images.unsplash.com/photo-1644546971385-83fa1d539a3e",
  heroImageAlt: 'Great Wall of China winding through green mountains under clear blue sky',
  tagline: 'Rising Global Power & Innovation Leader',
  highlights: [
    'PKU #17 & Tsinghua #20 globally (QS 2025)',
    'CSC Scholarship: 50,000+ awards/year',
    'Tuition: ¥15,000–¥45,000/year ($2,100–$6,200)',
    'Learn Mandarin in immersive environment',
  ],
  currency: 'CNY',
  currencySymbol: '¥',
  studentCount: 12000,
  topUniversities: 52,
  region: 'asia',
  language: 'mandarin',
  avgTuition: 5000,
  avgLiving: 8000,
  visaProcessing: '4-6 weeks',
  workRights: 'On-campus only'
},
{
  id: 'japan',
  name: 'Japan',
  flag: "https://img.rocket.new/generatedImages/rocket_gen_img_1cbc8e9c0-1764746751204.png",
  flagAlt: 'Japanese flag with white background and red circle',
  heroImage: "https://images.unsplash.com/photo-1681317474435-91e0f5065abf",
  heroImageAlt: 'Mount Fuji with cherry blossoms in foreground and traditional Japanese pagoda',
  tagline: 'Technology Innovation & Cultural Tradition',
  highlights: [
    'UTokyo #28 & Kyoto University #46 globally (QS 2025)',
    'MEXT Scholarship: Full tuition + ¥117,000–¥145,000/month',
    'National university tuition: ¥535,800/year ($3,600)',
    '28 hrs/week work rights during studies',
  ],
  currency: 'JPY',
  currencySymbol: '¥',
  studentCount: 4500,
  topUniversities: 35,
  region: 'asia',
  language: 'japanese',
  avgTuition: 8000,
  avgLiving: 14000,
  visaProcessing: '3-5 weeks',
  workRights: '28 hrs/week during studies'
},
{
  id: 'vietnam',
  name: 'Vietnam',
  flag: "https://images.unsplash.com/photo-1702378946176-4a66fa1d9c20",
  flagAlt: 'Vietnamese flag with red background and yellow star',
  heroImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1d6c6b838-1772253351479.png",
  heroImageAlt: 'Ha Long Bay in Vietnam with limestone karsts and emerald waters with traditional boats',
  tagline: 'Emerging Education Hub & Low Cost Living',
  highlights: [
    'Tuition: ₫20,000,000–₫50,000,000/year ($800–$2,000)',
    'Living costs: ₫7,000,000–₫15,900,000/month ($280–$640)',
    'GDP growth: 6–7% annually',
    'VIED Government Scholarship available',
  ],
  currency: 'VND',
  currencySymbol: '₫',
  studentCount: 2800,
  topUniversities: 18,
  region: 'asia',
  language: 'english',
  avgTuition: 3500,
  avgLiving: 5000,
  visaProcessing: '2-3 weeks',
  workRights: 'Limited opportunities'
},
{
  id: 'philippines',
  name: 'Philippines',
  flag: "https://img.rocket.new/generatedImages/rocket_gen_img_105172672-1772660697362.png",
  flagAlt: 'Philippine flag with blue and red horizontal stripes, white triangle, and sun with stars',
  heroImage: "https://images.unsplash.com/photo-1621692359226-070bd08a954d",
  heroImageAlt: 'Tropical beach in Philippines with white sand, turquoise water, and palm trees',
  tagline: 'English Education & Tropical Paradise',
  highlights: [
    '3rd highest English proficiency in Asia',
    'Tuition: ₱30,000–₱180,000/year ($530–$3,200)',
    'WHO-recognized medical degrees (MD)',
    'Living costs: ₱17,000–₱47,000/month ($300–$840)',
  ],
  currency: 'PHP',
  currencySymbol: '₱',
  studentCount: 3500,
  topUniversities: 22,
  region: 'asia',
  language: 'english',
  avgTuition: 2500,
  avgLiving: 6000,
  visaProcessing: '2-4 weeks',
  workRights: 'Limited during studies'
},
{
  id: 'saudi-arabia',
  name: 'Saudi Arabia',
  flag: "https://images.unsplash.com/photo-1675855547482-16834b36d4d9",
  flagAlt: 'Saudi Arabian flag with green background and white Arabic inscription and sword',
  heroImage: "https://img.rocket.new/generatedImages/rocket_gen_img_18e777df0-1772583417028.png",
  heroImageAlt: 'Modern Riyadh skyline with Kingdom Centre Tower and contemporary architecture',
  tagline: 'Modern Education & Generous Scholarships',
  highlights: [
    'KFUPM #186 & KAUST #166 globally (QS 2025)',
    'SACM Scholarship: Full tuition + accommodation + flights',
    'Vision 2030 – massive investment in education',
    'Tax-free income and benefits',
  ],
  currency: 'SAR',
  currencySymbol: 'SAR',
  studentCount: 2100,
  topUniversities: 15,
  region: 'middle-east',
  language: 'arabic',
  avgTuition: 0,
  avgLiving: 9000,
  visaProcessing: '4-6 weeks',
  workRights: 'Restricted'
},
{
  id: 'south-korea',
  name: 'South Korea',
  flag: "https://img.rocket.new/generatedImages/rocket_gen_img_1966e2cc7-1765130375507.png",
  flagAlt: 'South Korean flag with white background, red and blue yin-yang symbol, and black trigrams',
  heroImage: "https://images.unsplash.com/photo-1678888703077-0813878c6d78",
  heroImageAlt: 'Seoul cityscape with Namsan Tower, modern skyscrapers, and traditional architecture blend',
  tagline: 'Advanced Technology & K-Culture Excellence',
  highlights: [
    'SNU #31 & KAIST #47 globally (QS 2025)',
    'KGSP Scholarship: Full tuition + ₩900,000/month',
    'Tuition: ₩4,000,000–₩10,000,000/year',
    'D-10 job-seeking visa after graduation',
  ],
  currency: 'KRW',
  currencySymbol: '₩',
  studentCount: 3800,
  topUniversities: 30,
  region: 'asia',
  language: 'korean',
  avgTuition: 7000,
  avgLiving: 11000,
  visaProcessing: '3-5 weeks',
  workRights: '20 hrs/week during studies'
},
{
  id: 'ireland',
  name: 'Ireland',
  flag: "https://images.unsplash.com/photo-1545066230-54c1edc1cdf2",
  flagAlt: 'Irish flag with green, white, and orange vertical stripes',
  heroImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1be5585fc-1772113931761.png",
  heroImageAlt: 'Cliffs of Moher in Ireland with dramatic Atlantic Ocean views and green coastal landscape',
  tagline: 'English-Speaking Gateway to Europe',
  highlights: [
    'TCD #81 globally (QS 2025) – English-speaking EU',
    'Home to Google, Apple, Meta, LinkedIn, Pfizer HQs',
    'Stamp 1G post-study visa: up to 2 years',
    'Tuition: €12,000–€25,000/year',
  ],
  currency: 'EUR',
  currencySymbol: '€',
  studentCount: 3200,
  topUniversities: 20,
  region: 'europe',
  language: 'english',
  avgTuition: 12000,
  avgLiving: 12000,
  visaProcessing: '4-8 weeks',
  workRights: '20 hrs/week during studies'
}];


export default function CountryDestinationsInteractive() {
  const [filters, setFilters] = useState<FilterState>({
    region: 'all',
    language: 'all',
    budget: 'all',
    searchQuery: ''
  });

  const handleFilterChange = useCallback((newFilters: FilterState) => {
    setFilters(newFilters);
  }, []);

  const filteredCountries = countries.filter((country) => {
    const matchesRegion = filters.region === 'all' || country.region === filters.region;
    const matchesLanguage = filters.language === 'all' || country.language === filters.language;

    let matchesBudget = true;
    if (filters.budget === 'low') {
      matchesBudget = country.avgTuition < 10000;
    } else if (filters.budget === 'medium') {
      matchesBudget = country.avgTuition >= 10000 && country.avgTuition <= 25000;
    } else if (filters.budget === 'high') {
      matchesBudget = country.avgTuition > 25000;
    }

    const matchesSearch = filters.searchQuery === '' ||
    country.name.toLowerCase().includes(filters.searchQuery.toLowerCase()) ||
    country.tagline.toLowerCase().includes(filters.searchQuery.toLowerCase());

    return matchesRegion && matchesLanguage && matchesBudget && matchesSearch;
  });

  return (
    <>
      <section className="py-16 bg-background">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <FilterSection onFilterChange={handleFilterChange} />
          
          {filteredCountries.length === 0 ?
          <div className="text-center py-16">
              <p className="text-xl text-muted-foreground mb-4">No destinations match your filters</p>
              <button
              onClick={() => setFilters({ region: 'all', language: 'all', budget: 'all', searchQuery: '' })}
              className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium">
                Clear Filters
              </button>
          </div> :

          <>
              <div className="mb-6 text-muted-foreground">
                Showing {filteredCountries.length} of {countries.length} destinations
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCountries.map((country) =>
              <CountryCard key={country.id} country={country} />
              )}
              </div>
            </>
          }
        </div>
      </section>

      <ComparisonTool countries={countries} />
    </>);

}